import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { AccountSummary, AuthErrorResponse } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { getSessionAccount } from '@/lib/auth'
import { Seo } from '@/components/generals/seo'
import { Button, buttonVariants } from '@/components/ui/button'
import AuthLayout from '@/components/auth/auth-layout'

import {
    CalendarCheck,
    CircleCheck,
    FlaskConical,
    House,
    LoaderCircle,
    LogOut,
    Ticket
} from 'lucide-react'

import {
    AuthDivider,
    AuthHeading,
    FormAlert,
    PasswordField,
    TextField
} from '@/components/auth/form-controls'

import {
    validateEmail,
    validateLoginPassword
} from '@/lib/auth-validation'

type LoginResponse = { ok: true, account: AccountSummary } | AuthErrorResponse

type Alert = { title: string, message: string }

interface MasukProps {
    account: AccountSummary | null
}

const features = [
    { icon: CalendarCheck, text: 'Buat janji dengan dokter tanpa antre di loket' },
    { icon: Ticket, text: 'Pantau nomor antrean dan estimasi giliran' },
    { icon: FlaskConical, text: 'Lihat hasil lab setelah dirilis dokter' }
]

/** Summary shown above the form, following the validation states in the design */
const summarizeErrors = (email: string, password: string, emailError: string | null, passwordError: string | null): Alert => {
    const emailEmpty = !email.trim()
    const passwordEmpty = !password

    if (emailEmpty && passwordEmpty) {
        return { title: 'Lengkapi 2 kolom yang wajib diisi', message: 'Email dan kata sandi belum diisi. Lengkapi keduanya untuk masuk.' }
    }

    if (emailError && passwordError) {
        return { title: 'Ada isian yang perlu diperbaiki', message: 'Periksa 2 kolom yang ditandai merah, lalu coba lagi.' }
    }

    if (emailEmpty) return { title: 'Lengkapi 1 kolom yang wajib diisi', message: 'Email belum diisi. Lengkapi untuk masuk.' }
    if (passwordEmpty) return { title: 'Lengkapi 1 kolom yang wajib diisi', message: 'Kata sandi belum diisi. Lengkapi untuk masuk.' }

    return { title: 'Periksa format email', message: 'Ada 1 kolom yang perlu diperbaiki: email.' }
}

interface SignedInProps {
    account: AccountSummary
    onSignedOut: () => void
}

const SignedIn: FC<SignedInProps> = ({ account, onSignedOut }): ReactNode => {
    const [signingOut, setSigningOut] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const signOut = async (): Promise<void> => {
        setSigningOut(true)
        setError(null)

        try {
            const response = await fetch('/api/auth/keluar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}'
            })

            if (!response.ok) throw new Error('Sign out failed')

            onSignedOut()
        }
        catch {
            setError('Belum bisa keluar. Periksa koneksi internet Anda, lalu coba lagi.')
        }
        finally {
            setSigningOut(false)
        }
    }

    return (
        <div>
            <span className='flex size-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600'>
                <CircleCheck className='size-6' aria-hidden='true' />
            </span>

            <div className='mt-6'>
                <AuthHeading
                    title='Anda sudah masuk'
                    description={(
                        <>
                            Selamat datang kembali,
                            {' '}
                            <span data-mdlfy-cms={`akun_pasien:${account.id}:nama_lengkap`} className='font-semibold text-ink'>
                                {account.nama}
                            </span>
                            .
                        </>
                    )}
                />
            </div>

            <div className='mt-6 rounded-2xl bg-brand-50/70 p-5 text-center ring-1 ring-brand-100 ring-inset'>
                <p className='text-sm text-muted-foreground'>
                    Nomor rekam medis Anda
                </p>

                <p className='mt-1 font-mono text-2xl font-bold tracking-wider text-ink'>
                    {account.noRekamMedis}
                </p>

                <p className='mt-1 text-sm break-all text-muted-foreground'>
                    Masuk sebagai
                    {' '}
                    {account.email}
                </p>
            </div>

            {error && (
                <FormAlert title='Gagal keluar' className='mt-5'>
                    {error}
                </FormAlert>
            )}

            <div className='mt-6 grid gap-3 sm:grid-cols-2'>
                <Link href='/' className={buttonVariants({ size: 'lg' })}>
                    <House className='size-5' aria-hidden='true' />
                    Ke beranda
                </Link>

                <Button variant='outline' size='lg' onClick={() => void signOut()} disabled={signingOut}>
                    {signingOut ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' /> : <LogOut className='size-5' aria-hidden='true' />}
                    {signingOut ? 'Keluar...' : 'Keluar'}
                </Button>
            </div>
        </div>
    )
}

const Masuk: FC<MasukProps> = ({ account: initialAccount }): ReactNode => {
    const [account, setAccount] = useState<AccountSummary | null>(initialAccount)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [errors, setErrors] = useState<{ email?: string, password?: string }>({})
    const [alert, setAlert] = useState<Alert | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const emailError = validateEmail(email)
        const passwordError = validateLoginPassword(password)

        setErrors({ email: emailError ?? undefined, password: passwordError ?? undefined })

        if (emailError || passwordError) {
            setAlert(summarizeErrors(email, password, emailError, passwordError))
            document.getElementById(emailError ? 'masuk-email' : 'masuk-password')?.focus()
            return
        }

        setAlert(null)
        setSubmitting(true)

        try {
            const response = await fetch('/api/auth/masuk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            })

            const data = await response.json() as LoginResponse

            if (data.ok) {
                setAccount(data.account)
                setPassword('')
                window.scrollTo({ top: 0 })
                return
            }

            setErrors({ email: data.fields?.email, password: data.fields?.password })
            setAlert({ title: data.title, message: data.message })
        }
        catch {
            setAlert({ title: 'Koneksi terputus', message: 'Periksa koneksi internet Anda, lalu coba lagi.' })
        }
        finally {
            setSubmitting(false)
        }
    }

    return (
        <AuthLayout
            title='Selamat datang kembali di layanan klinik.'
            description='Masuk untuk membuat janji, melihat riwayat kunjungan dan hasil lab, serta mengurus layanan klinik dengan lebih mudah.'
            features={features}
        >
            <Seo title='Masuk' />

            {account ? (
                <SignedIn
                    account={account}
                    onSignedOut={() => {
                        setAccount(null)
                        setEmail('')
                        setErrors({})
                        setAlert(null)
                    }}
                />
            ) : (
                <form onSubmit={handleSubmit} noValidate>
                    <AuthHeading title='Masuk ke akun' description='Gunakan email dan kata sandi yang Anda daftarkan.' />

                    {alert && (
                        <FormAlert title={alert.title} className='mt-6'>
                            {alert.message}
                        </FormAlert>
                    )}

                    <div className='mt-6 grid gap-5'>
                        <TextField
                            id='masuk-email'
                            label='Email'
                            type='email'
                            inputMode='email'
                            autoComplete='email'
                            placeholder='nama@contoh.com'
                            hint='Gunakan email pemilik akun.'
                            error={errors.email}
                            value={email}
                            onChange={event => {
                                setEmail(event.target.value)
                                if (errors.email) setErrors(current => ({ ...current, email: undefined }))
                            }}
                        />

                        <PasswordField
                            id='masuk-password'
                            label='Kata sandi'
                            autoComplete='current-password'
                            placeholder='Masukkan kata sandi'
                            hint='Kata sandi yang Anda buat saat mendaftar.'
                            error={errors.password}
                            value={password}
                            onChange={event => {
                                setPassword(event.target.value)
                                if (errors.password) setErrors(current => ({ ...current, password: undefined }))
                            }}
                        />
                    </div>

                    <div className='mt-4 flex justify-end'>
                        <Link href='/lupa-kata-sandi' className='rounded-md text-sm font-bold text-brand-700 outline-none hover:underline focus-visible:ring-4 focus-visible:ring-ring/30'>
                            Lupa kata sandi?
                        </Link>
                    </div>

                    <Button type='submit' size='lg' className='mt-6 w-full' disabled={submitting}>
                        {submitting && <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />}
                        {submitting ? 'Memeriksa...' : 'Masuk'}
                    </Button>

                    <AuthDivider />

                    <p className='text-center text-[15px] text-ink-soft'>
                        Belum punya akun?
                        {' '}
                        <Link href='/daftar' className='font-bold text-brand-700 hover:underline'>
                            Daftar
                        </Link>
                    </p>
                </form>
            )}
        </AuthLayout>
    )
}

export const getServerSideProps: GetServerSideProps<MasukProps> = async ({ req }) => {
    const account = await getSessionAccount(req)

    return { props: { account } }
}

export default Masuk
